/**
 * `03/CRF/Work Confirmation` (37:7004) — `/countries/:cc/crfs/confirmations`.
 *
 * The register of CRFs whose confirmation has been RECORDED as sent and which
 * are now waiting on the customer. Rows come off ONE payload
 * (`GET /countries/:cc/crfs/awaiting-confirmation`), so the header count, the
 * rows and the pager cannot disagree with each other — the QuotesListPage
 * ruling, held here by the simpler arrangement: no `keepPreviousData`, so a
 * page step shows the skeleton rather than another page's rows wearing this
 * page's number.
 *
 * ===========================================================================
 * POINTS CONFIRMED IS TEXT, AND THE BAR BESIDE IT IS DECOR
 * ===========================================================================
 * The house rule pinned in `sales-orders.test.tsx` (header, and the assertions
 * at its lines 174-197): NO `role="progressbar"` and no `<progress>` element.
 * A progressbar announces work in flight; nothing is running here — the number
 * is a settled fact about customer decisions. So the accessible value is the
 * TEXT "3 / 5" (`tnum`), and the 60px micro-bar beside it is two nested divs
 * under `aria-hidden="true"`: paint, not a role. No bar-like element exists in
 * the app to copy classes from (checked: the only `h-1.5` bars are skeletons
 * and the dot grid), so the fill uses existing tokens only — `bg-ok-fg` when
 * every point is confirmed, `bg-indigo` (the accent) otherwise, on a
 * `bg-subtle` track.
 *
 * ===========================================================================
 * THE LINK IS ABSOLUTE, DELIBERATELY
 * ===========================================================================
 * From the ROUTE `crfs/confirmations` (a flat sibling under the pathless
 * `CrfProviders` layout — `routes.tsx`), a relative `../{id}/confirmation`
 * resolves against the route tree, not the URL: `..` lands on the layout's
 * parent, `/countries/:cc`, producing `/countries/:cc/{id}/confirmation` —
 * wrong. Every other register builds country-prefixed hrefs absolutely
 * (`QuotesListPage`, `SalesOrdersListPage`), so this one does too.
 *
 * ===========================================================================
 * WAITING IS A PLAIN PILL, NOT `DaysPill`
 * ===========================================================================
 * `DaysPill` hard-codes its threshold to `OVERDUE_DAYS_IN_STAGE` (7 days) via
 * `isOverdueInStage` and exposes no threshold prop; the design's accent here
 * sits at 9 days. Reusing the pill would repaint a 7-day judgment about
 * INQUIRY stages onto customer-reply waits, so the cell renders the neutral
 * pill treatment with the text "Nd waiting" and no colour logic — recorded as
 * the briefed fallback rather than a missing feature.
 */

import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import type { CountryCode } from '../../domain/countries';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Button, CountryBadge, EmptyState, SkeletonRows } from '../../components/ui';
import { daysSince } from '../../domain/quotes';
import { relativeTime } from '../../domain/events';
import { formatDateLong } from '../inquiries/format';
import { cn } from '../../lib/cn';
import { crfErrorText } from './errors';
import { crfKeys, fetchAwaitingConfirmation } from './api';
import type { AwaitingConfirmationRowDto } from './api';

const PER_PAGE = 50;

/** This same register at page 1 — every other search parameter kept. */
function firstPageHref(countryCode: string, params: URLSearchParams): string {
  const patch = new URLSearchParams(params);
  patch.delete('page');
  const query = patch.toString();
  return `/countries/${countryCode}/crfs/confirmations${query === '' ? '' : `?${query}`}`;
}

export function WorkConfirmationPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const canView = can(countryCode, 'crf.view');
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const list = useQuery({
    queryKey: crfKeys.awaiting(countryCode, page),
    queryFn: ({ signal }) =>
      fetchAwaitingConfirmation(countryCode, { page, perPage: PER_PAGE }, signal),
    enabled: canView,
  });

  if (!canView) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Work confirmations are not available to this account">
          Viewing CRFs in {countryCode} needs the CRF permission. Ask an administrator to
          widen your access.
        </Alert>
      </div>
    );
  }

  const goToPage = (next: number): void => {
    const patch = new URLSearchParams(params);
    if (next <= 1) patch.delete('page');
    else patch.set('page', String(next));
    setParams(patch);
  };

  const shown = list.data;

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Work Confirmation"
        subtitle={
          shown === undefined ? undefined : `${String(shown.total)} awaiting customer reply`
        }
      />

      {list.isPending ? (
        <SkeletonRows label="Loading work confirmations" />
      ) : list.isError ? (
        <Alert tone="danger" title="The confirmation register could not be loaded">
          {crfErrorText(list.error)}
        </Alert>
      ) : shown === undefined ? null : shown.total > 0 && shown.data.length === 0 ? (
        /* An empty PAGE of a non-empty result set is navigation, not emptiness —
         * the QuotesListPage V3 ruling, held here in its minimal form. */
        <div className="flex flex-col items-start gap-2 rounded-md border border-line bg-surface p-5">
          <p className="text-body text-fg">
            Page {String(shown.page)} is past the end of this register.
          </p>
          <Link
            to={firstPageHref(countryCode, params)}
            className="text-body13 text-indigo hover:underline"
          >
            Back to the first page
          </Link>
        </div>
      ) : shown.data.length === 0 ? (
        <div className="rounded-md border border-dashed border-line bg-surface">
          <EmptyState title="Nothing is waiting on a customer right now." />
        </div>
      ) : (
        <ConfirmationTable countryCode={countryCode} rows={shown.data} />
      )}

      {shown !== undefined && shown.total > 0 ? (
        <Pager
          page={shown.page}
          perPage={shown.perPage}
          total={shown.total}
          onPage={goToPage}
          busy={list.isFetching}
        />
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── table ── */

function ConfirmationTable({
  countryCode,
  rows,
}: {
  countryCode: CountryCode;
  rows: readonly AwaitingConfirmationRowDto[];
}): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[840px] border-collapse">
        <caption className="sr-only">
          CRFs awaiting customer confirmation in {countryCode}
        </caption>
        <thead>
          <tr className="border-b border-line">
            <th scope="col" className="px-4 py-2.5 text-left text-th uppercase text-fg-secondary">
              CRF ID
            </th>
            <th scope="col" className="px-4 py-2.5 text-left text-th uppercase text-fg-secondary">
              Customer
            </th>
            <th scope="col" className="px-4 py-2.5 text-left text-th uppercase text-fg-secondary">
              Points confirmed
            </th>
            <th scope="col" className="px-4 py-2.5 text-left text-th uppercase text-fg-secondary">
              Developer
            </th>
            <th scope="col" className="px-4 py-2.5 text-left text-th uppercase text-fg-secondary">
              Sent on
            </th>
            <th scope="col" className="px-4 py-2.5 text-left text-th uppercase text-fg-secondary">
              Waiting
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const waiting = daysSince(row.confirmation_sent_at);
            const full = row.points_total > 0 && row.points_confirmed >= row.points_total;
            const pct =
              row.points_total > 0
                ? Math.min(100, (row.points_confirmed / row.points_total) * 100)
                : 0;
            return (
              <tr key={row.id} className="border-b border-line last:border-b-0 hover:bg-subtle">
                {/* The badge leads the identity column — the one rule across
                 * every list in the app (QuoteTable, Phase 3a note). */}
                <td className="px-4 py-3">
                  <span className="flex items-center gap-1.5">
                    <CountryBadge code={countryCode} />
                    <Link
                      to={`/countries/${countryCode}/crfs/${row.id}/confirmation`}
                      className="font-mono text-mono text-fg underline underline-offset-2 hover:decoration-2"
                    >
                      {row.doc_no}
                    </Link>
                  </span>
                </td>
                <td className="px-4 py-3 text-body13 text-fg">{row.party_name}</td>
                <td className="px-4 py-3">
                  <span className="flex items-center gap-2">
                    {/* The accessible value is this TEXT. The bar below is decor —
                     * see the file header for the pinned no-progressbar rule. */}
                    <span className="tnum text-body13 text-fg">
                      {`${String(row.points_confirmed)} / ${String(row.points_total)}`}
                    </span>
                    <div
                      aria-hidden="true"
                      className="h-1.5 w-[60px] shrink-0 overflow-hidden rounded-pill bg-subtle ring-1 ring-inset ring-line"
                    >
                      <div
                        className={cn('h-full rounded-pill', full ? 'bg-ok-fg' : 'bg-indigo')}
                        style={{ width: `${String(pct)}%` }}
                      />
                    </div>
                  </span>
                </td>
                <td className="px-4 py-3 text-body13">
                  {row.developer === null ? (
                    <span className="text-fg-muted">Unassigned</span>
                  ) : (
                    <span className="text-fg">{row.developer.name}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="flex flex-col gap-0.5">
                    <span className="text-body13 text-fg-secondary">
                      {formatDateLong(row.confirmation_sent_at)}
                    </span>
                    <span className="text-label text-fg-muted">
                      {relativeTime(row.confirmation_sent_at)}
                    </span>
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="flex flex-col items-start gap-1">
                    <WaitingPill days={waiting} />
                    {row.confirmation_reminders > 0 ? (
                      <span className="text-label text-fg-muted">
                        {`${String(row.confirmation_reminders)} ${
                          row.confirmation_reminders === 1 ? 'reminder' : 'reminders'
                        } sent`}
                      </span>
                    ) : null}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * "9d waiting" in the neutral pill treatment — `DaysPill`'s own neutral pair,
 * without its 7-day accent (the wrong threshold for this column; file header).
 */
function WaitingPill({ days }: { days: number | null }): React.JSX.Element {
  if (days === null) {
    return (
      <span
        className="inline-flex items-center rounded-pill border border-line bg-subtle px-2.5 py-1 text-label font-medium text-fg-muted"
        title="Waiting time is unknown"
      >
        —
      </span>
    );
  }
  return (
    <span className="tnum inline-flex items-center whitespace-nowrap rounded-pill border border-line bg-subtle px-2.5 py-1 text-label font-medium text-fg-secondary">
      {`${String(days)}d waiting`}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────── pager ── */

/** The register pager — QuotesListPage's `Pager`, minus the `behind` machinery
 * this screen does not need (no `keepPreviousData`, so a moving page shows the
 * skeleton and the pager leaves the screen with the payload it described). */
function Pager({
  page,
  perPage,
  total,
  onPage,
  busy,
}: {
  page: number;
  perPage: number;
  total: number;
  onPage: (page: number) => void;
  busy: boolean;
}): React.JSX.Element | null {
  const pages = Math.max(1, Math.ceil(total / perPage));
  if (pages === 1) return null;
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);
  return (
    <div className="flex items-center justify-between gap-3">
      <p className="tnum text-label text-fg-secondary">
        {from > total
          ? `${String(total)} in total`
          : `${String(from)}–${String(to)} of ${String(total)}`}
      </p>
      <div className="flex items-center gap-2">
        <Button variant="ghost" disabled={page <= 1 || busy} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <Button variant="ghost" disabled={page >= pages || busy} onClick={() => onPage(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}
